import { Component, EventEmitter, Input, OnChanges, Output } from '@angular/core';
import { CibleRecomptage, MOTIFS_RECOMPTAGE, MotifRecomptage, Recomptage, RecomptageService } from 'src/app/core/services/entities/recomptage.service';
import { RoleUtil } from './role.util';
import { nouvelleClientRef } from './client-ref';

/**
 * Recomptage d'un troupeau ou d'un lot de poissons (gestionnaire, administrateur) : nombre réel, écart en
 * direct avec l'effectif, motif (il décide du mouvement enregistré : morts, naissances, ajustement…).
 * Affiche le dernier comptage.
 */
@Component({
  selector: 'bay-recomptage',
  template: `
    <div class="rounded-4 p-3 mb-3" style="background:#f8faf5;" data-etat="recomptage">
      <div class="d-flex flex-wrap justify-content-between align-items-center gap-2">
        <div style="font-size:13px;color:#374151;">
          <span class="material-symbols-outlined align-middle me-1" style="font-size:18px;color:#0d631b;">fact_check</span>
          <ng-container *ngIf="dernier; else jamais">
            Dernier comptage : <strong>{{ dernier.compte | number }}</strong> le {{ dernier.date | date:'dd/MM/yyyy' }}
            <span *ngIf="dernier.ecart" [style.color]="dernier.ecart < 0 ? '#b91c1c' : '#0d631b'"> ({{ dernier.ecart > 0 ? '+' : '' }}{{ dernier.ecart }})</span>
            <span *ngIf="dernier.createdBy"> · {{ dernier.createdBy }}</span>
          </ng-container>
          <ng-template #jamais>Pas encore recompté</ng-template>
        </div>
        <button *ngIf="peutRecompter && !ouvert" type="button" class="btn btn-sm fw-semibold" data-test="recompter"
                style="background:#0d631b;color:#fff;border-radius:10px;" (click)="ouvrir()">Recompter</button>
      </div>

      <form *ngIf="ouvert" class="mt-3" (ngSubmit)="enregistrer()" data-etat="formulaire-recomptage">
        <div class="row g-2">
          <div class="col-6">
            <label class="form-label small fw-semibold" for="rc-compte">Compté *</label>
            <input id="rc-compte" type="number" min="0" class="form-control" name="compte" [(ngModel)]="compte" (ngModelChange)="ajusterMotif()" required>
          </div>
          <div class="col-6">
            <label class="form-label small fw-semibold">Écart</label>
            <div class="form-control-plaintext fw-bold" [style.color]="ecart < 0 ? '#b91c1c' : ecart > 0 ? '#0d631b' : '#374151'" data-etat="ecart">
              {{ compte == null ? '—' : (ecart > 0 ? '+' : '') + ecart }} <span class="fw-normal text-muted">sur {{ attendu }}</span>
            </div>
          </div>
          <div class="col-6">
            <label class="form-label small fw-semibold" for="rc-date">Date</label>
            <input id="rc-date" type="date" class="form-control" name="date" [(ngModel)]="date">
          </div>
          <div class="col-6" *ngIf="ecart !== 0">
            <label class="form-label small fw-semibold" for="rc-motif">Motif de l'écart *</label>
            <select id="rc-motif" class="form-select" name="motif" [(ngModel)]="motif" required>
              <option [ngValue]="null" disabled>Choisir…</option>
              <option *ngFor="let m of motifs" [ngValue]="m.code">{{ m.libelle }}</option>
            </select>
          </div>
          <div class="col-12">
            <label class="form-label small fw-semibold" for="rc-note">Note{{ motif === 'AUTRE' ? ' *' : '' }}</label>
            <input id="rc-note" class="form-control" name="note" [(ngModel)]="note" placeholder="ex : comptage au portail, 2 agneaux trouvés morts">
          </div>
        </div>
        <div *ngIf="erreur" class="text-danger small mt-2" role="alert">{{ erreur }}</div>
        <div class="d-flex gap-2 mt-2">
          <button type="submit" class="btn btn-sm btn-success" [disabled]="envoi">Enregistrer le comptage</button>
          <button type="button" class="btn btn-sm btn-light" (click)="ouvert = false">Annuler</button>
        </div>
      </form>
    </div>
  `
})
export class RecomptageComponent implements OnChanges {
  @Input() cible: CibleRecomptage = 'TROUPEAU';
  @Input() cibleId?: number;
  @Input() attendu = 0;
  @Input() fermeId?: number;
  /** Comptage enregistré : la fiche recharge l'effectif et ses mouvements */
  @Output() recompte = new EventEmitter<Recomptage>();

  dernier?: Recomptage;
  ouvert = false;
  compte: number | null = null;
  motif: MotifRecomptage | null = null;
  note = '';
  date = this.aujourdhui();
  erreur: string | null = null;
  envoi = false;
  private ref = nouvelleClientRef();

  constructor(private service: RecomptageService) {}

  get peutRecompter(): boolean { return RoleUtil.gereExploitation(); }

  get ecart(): number { return this.compte == null ? 0 : this.compte - (this.attendu || 0); }

  /** Motifs compatibles avec le sens de l'écart (naissances : troupeau seulement) */
  get motifs() {
    return MOTIFS_RECOMPTAGE.filter(m => (this.ecart > 0 ? m.hausse : m.baisse) && (!m.troupeauSeulement || this.cible === 'TROUPEAU'));
  }

  ngOnChanges(): void {
    if (this.cibleId) {
      this.service.historique(this.cible, this.cibleId).subscribe({
        next: h => this.dernier = (h || []).slice().sort((a, b) => (b.date || '').localeCompare(a.date || '') || (b.createdAt || '').localeCompare(a.createdAt || ''))[0],
        error: () => this.dernier = undefined
      });
    }
  }

  ouvrir(): void {
    this.ouvert = true;
    this.compte = null;
    this.motif = null;
    this.note = '';
    this.date = this.aujourdhui();
    this.erreur = null;
    this.ref = nouvelleClientRef();
  }

  ajusterMotif(): void {
    if (this.motif && !this.motifs.some(m => m.code === this.motif)) { this.motif = null; }
  }

  enregistrer(): void {
    this.erreur = null;
    if (this.compte == null || this.compte < 0) { this.erreur = 'Saisissez le nombre compté.'; return; }
    if (this.ecart !== 0 && !this.motif) { this.erreur = 'Choisissez le motif de l\'écart.'; return; }
    if (this.motif === 'AUTRE' && !this.note.trim()) { this.erreur = 'Précisez le motif dans la note.'; return; }
    if (!this.cibleId) { return; }
    this.envoi = true;
    this.service.recompter(this.cible, this.cibleId, {
      clientRef: this.ref, fermeId: this.fermeId, compte: this.compte, attendu: this.attendu,
      motif: this.ecart !== 0 ? this.motif : null, note: this.note.trim() || undefined, date: this.date,
    }).subscribe({
      next: r => { this.envoi = false; this.ouvert = false; this.dernier = r; this.recompte.emit(r); },
      error: e => { this.envoi = false; this.erreur = e?.error?.message || 'Le comptage n\'a pas pu être enregistré.'; }
    });
  }

  private aujourdhui(): string {
    const d = new Date();
    const p = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
  }
}
